// =========================================
// geo.js — GeoJSON & geofence helpers (puri, testabili)
// =========================================
//
// coords resta il formato CANONICO in tutto il codice: [lat, lng] (convenzione
// Leaflet usata da marker, polyline, haversine, cron geo-assign). Il GeoJSON
// usa l'ordine opposto [lng, lat]: questi adapter sono l'UNICO punto in cui
// avviene la conversione, così il resto del codice non rischia l'inversione.
//
// La geofence è una UNION DISCRIMINATA da `kind`. Oggi esiste solo 'circle';
// la struttura è pensata per accogliere 'polygon' (o altre geometrie) in futuro
// senza toccare i call-site: basta aggiungere un `case` in geofenceContains.

import { haversineKm } from './geoHelpers';

/**
 * Guard coordinate: true solo per una tupla [lat,lng] con due numeri finiti.
 * `coords: []` è truthy in JS quindi `!coords` non basta — serve questo check.
 * Fonte unica usata da mappe (Leaflet/Mapbox) e ovunque servano coords valide.
 */
export const isValidCoords = (c) =>
  Array.isArray(c) && c.length === 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]);

/** [lat,lng] → GeoJSON Point { type, coordinates:[lng,lat] }. null se invalido. */
export const toGeoJSONPoint = (coords) => {
  if (!Array.isArray(coords) || coords.length !== 2) return null;
  const [lat, lng] = coords;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { type: 'Point', coordinates: [lng, lat] };
};

/** GeoJSON Point → [lat,lng]. null se invalido. */
export const coordsFromGeoJSONPoint = (geo) => {
  if (!geo || geo.type !== 'Point' || !Array.isArray(geo.coordinates)) return null;
  const [lng, lat] = geo.coordinates;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return [lat, lng];
};

/**
 * Accessor UNICO delle coordinate di un'entità: legge il campo canonico `geo`
 * (GeoJSON Point) e ritorna [lat,lng] per l'uso interno (Leaflet/Mapbox/haversine).
 * I documenti Firestore persistono SOLO `geo`; in memoria il resto del codice
 * continua a ragionare in [lat,lng]. null se l'entità non ha coordinate valide.
 */
export const coordsOf = (entity) => coordsFromGeoJSONPoint(entity?.geo);

/** Raggio di default per le geofence a cerchio, in metri. */
export const DEFAULT_GEOFENCE_RADIUS_M = 200;

/**
 * Costruisce una geofence a cerchio. `center` è [lat,lng], `radiusM` in metri.
 * Ritorna null se il centro è invalido o il raggio non è positivo.
 */
export const makeCircleGeofence = (center, radiusM = DEFAULT_GEOFENCE_RADIUS_M) => {
  if (!Array.isArray(center) || center.length !== 2) return null;
  if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) return null;
  const r = Number(radiusM);
  if (!Number.isFinite(r) || r <= 0) return null;
  return { kind: 'circle', center: [center[0], center[1]], radiusM: r };
};

/**
 * Approssima un cerchio (center [lat,lng], raggio in metri) come GeoJSON Polygon
 * con `steps` vertici. Coordinate in ordine GeoJSON [lng,lat], pronte per il
 * rendering su mappa (Mapbox GL è GeoJSON-native). null se input invalido.
 */
export const circleToPolygon = (center, radiusM, steps = 64) => {
  if (!Array.isArray(center) || !Number.isFinite(center[0]) || !Number.isFinite(center[1])) return null;
  if (!Number.isFinite(radiusM) || radiusM <= 0) return null;
  const [lat, lng] = center;
  const earthR = 6378137; // raggio terrestre medio in metri
  const dLat = (radiusM / earthR) * (180 / Math.PI);
  const dLng = dLat / Math.cos((lat * Math.PI) / 180);
  const ring = [];
  for (let i = 0; i <= steps; i++) {
    const theta = (i / steps) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(theta), lat + dLat * Math.sin(theta)]);
  }
  return { type: 'Polygon', coordinates: [ring] };
};

/**
 * Un punto [lat,lng] cade dentro la geofence?
 * Union discriminata da `kind`; un `kind` sconosciuto → false (fail-safe).
 * Per supportare nuove geometrie (es. 'polygon') aggiungere qui un `case`.
 */
export const geofenceContains = (coords, geofence) => {
  if (!geofence || typeof geofence !== 'object') return false;
  switch (geofence.kind) {
    case 'circle': {
      const d = haversineKm(coords, geofence.center);
      if (d == null) return false;
      return d * 1000 <= geofence.radiusM;
    }
    default:
      return false;
  }
};
